/** One leaf condition of a json-rules-engine rule. @public */
export interface RuleLeaf {
  fact: string;
  operator?: unknown;
  value?: unknown;
  /** Set by jsonfc on an evaluated rule. */
  result?: boolean;
  /** Under an odd number of `not`s, so the rule wants the condition to be false. */
  negated: boolean;
}

type Node = Record<string, unknown>;

const isNode = (value: unknown): value is Node =>
  typeof value === "object" && value !== null;

const asList = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  return isNode(value) ? [value] : [];
};

/** Leaf conditions under `all`, `any` and `not`, nested; `not` takes one condition or a list. @public */
export function ruleLeaves(node: unknown, negated = false): RuleLeaf[] {
  if (!isNode(node)) return [];
  if (typeof node.fact === "string") {
    const result = typeof node.result === "boolean" ? node.result : undefined;
    return [
      {
        fact: node.fact,
        operator: node.operator,
        value: node.value,
        result,
        negated,
      },
    ];
  }
  return [
    ...(Array.isArray(node.all) ? node.all : []).flatMap((c) =>
      ruleLeaves(c, negated)
    ),
    ...(Array.isArray(node.any) ? node.any : []).flatMap((c) =>
      ruleLeaves(c, negated)
    ),
    ...asList(node.not).flatMap((c) => ruleLeaves(c, !negated)),
  ];
}

/**
 * The facts that made a check fail: those whose conditions evaluated against
 * the rule (false, or true under `not`). Every fact in the result when jsonfc
 * reports no per-condition results, or none of them names a fact it returned.
 * @public
 */
export function failingFactIds(result: {
  facts?: Record<string, unknown>;
  check: { rule?: { conditions?: unknown } };
}): string[] {
  const all = Object.keys(result.facts ?? {});
  const failed = new Set(
    ruleLeaves(result.check.rule?.conditions)
      .filter((leaf) => leaf.result === leaf.negated)
      .map((leaf) => leaf.fact)
  );
  const picked = all.filter((id) => failed.has(id));
  return picked.length > 0 ? picked : all;
}
